import moment from 'moment-timezone';

import {
  minimumPaymentPlanInstallments,
  MiscChargeTypesPriorityOrder,
  PaymentPlanStatus,
  StatementStatus,
  AppointmentStatusMapping,
  APPOINTMENT_TYPE_TRANS_KEY,
  PAYMENT_PLAN_FREQUENCY_IDS,
  PatientRecallAppointmentStatusMapping,
  NotificationType,
  ReminderWorkFlowStatusTypes,
  messagesCategory
} from '../constants/Mapping';
import {environment} from '../environment/environment';
interface FullAddressProps {
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  country?: string;
  zip?: string;
}

import CryptoJS from 'crypto-js';

const addComma = (str: string): string => {
  let val = str;
  if (val) {
    val += ', ';
  }
  return val;
};

class HelperUtils {
  static fixTo2Decimals(value: any): any {
    if (isNaN(value)) {
      return value;
    }
    return (Math.round(value * 100) / 100).toFixed(2);
  }

  static fixTo2DecimalFloor(value: any): any {
    if (isNaN(value)) {
      return value;
    }
    return (Math.floor(value * 100) / 100).toFixed(2);
  }

  static formFullName(
    firstName?: string,
    lastName?: string,
    middleName?: string
  ): string {
    let fullName = '';
    if (firstName) {
      fullName += firstName;
    }
    if (middleName) {
      fullName += fullName ? ` ${middleName}` : middleName;
    }
    if (lastName) {
      fullName += fullName ? ` ${lastName}` : lastName;
    }
    return fullName;
  }

  static formCapitalizeFullName(
    firstName: string | undefined,
    lastName: string | undefined,
    middleName: string | undefined
  ): string {
    const nonEmptyNameParts = [firstName, middleName, lastName].filter(
      name => !!name
    );
    const capitalizedNameParts = nonEmptyNameParts.map(name =>
      HelperUtils.captilizeFirstLetter(name as string)
    );
    const fullName = capitalizedNameParts.join(' ');
    return fullName;
  }

  static formatPhoneNumber(phone: string): string {
    // if (phone && phone.length >= 10) {
    //   const phoneNumber: string = phone.replace(/\D/g, '');
    //   let start: number = phoneNumber.length - 4;
    //   const lineNumber = phoneNumber.substring(start, start + 4);
    //   start -= 3;
    //   const prefix = phoneNumber.substring(start, start + 3);
    //   start -= 3;
    //   const areaCode = phoneNumber.substring(start, start + 3);
    //   return '(' + areaCode + ') ' + prefix + '-' + lineNumber;
    // }
    return phone;
  }
  static formatDisplayDateTime(
    dateTimeVal: any,
    format = 'MM/DD/YYYY HH:mm'
  ): any {
    let value = '';
    if (dateTimeVal && moment(dateTimeVal).isValid()) {
      value = `${moment(dateTimeVal).format(format)} ${moment
        .tz(moment.tz.guess())
        .zoneAbbr()}`;
    }
    return value;
  }

  static formatDisplayDate(
    dateTimeVal: any,
    format: string = 'MM/DD/YYYY'
  ): any {
    let value = '';
    if (dateTimeVal && moment(dateTimeVal).isValid()) {
      value = moment(dateTimeVal).format(format);
    }
    return value;
  }

  static formatDisplayTime(dateTimeVal: any): any {
    let value = '';
    if (dateTimeVal && moment(dateTimeVal).isValid()) {
      value = `${moment(dateTimeVal).format('HH:mm')} ${moment
        .tz(moment.tz.guess())
        .zoneAbbr()}`;
    }
    return value;
  }

  static addTimeZoneToDate(
    date: string,
    format: string,
    timezone = 'America/Chicago'
  ): string {
    try {
      const value = moment.tz(date, format, timezone).format();
      return value;
    } catch (err: any) {
      return '';
    }
  }

  static shortName(firstName: string, lastName: string): string {
    let result = '';
    if (firstName) {
      result += firstName[0].toUpperCase();
    }
    if (lastName) {
      result += lastName[0].toUpperCase();
    }
    return result ? result : 'U';
  }

  // if no time is passed, it will default to the current datetime,
  // function will  return whether it is morning, afternoon or evening
  static dynamicSalutation(
    time?: string
  ): 'MORNING' | 'AFTERNOON' | 'EVENING' | '' {
    const timeMoment = time ? moment(time) : moment();
    if (timeMoment.isValid()) {
      const hour = timeMoment.hour();
      if (hour >= 2 && hour < 12) {
        return 'MORNING';
      } else if (hour >= 12 && hour < 17) {
        return 'AFTERNOON';
      } else {
        return 'EVENING';
      }
    }
    return '';
  }

  static formatFullAddress(address: FullAddressProps): string {
    let addr = '';
    if (address.line1) {
      addr += address.line1.trim();
    }
    if (address.line2) {
      addr = addComma(addr);
      addr += address.line2.trim();
    }
    if (address.city) {
      addr = addComma(addr);
      addr += address.city.trim();
    }
    if (address.state) {
      addr = addComma(addr);
      addr += address.state.trim();
    }
    if (address.country) {
      addr = addComma(addr);
      addr += address.country.trim();
    }
    if (address.zip) {
      addr += ' ' + address.zip.trim();
    }
    return addr;
  }

  static maskCardNumber(cardNumber: string, showChars?: number): string {
    let maskedCard = '',
      chars = 4;
    if (showChars && !isNaN(showChars)) {
      chars = Number(showChars);
    }
    if (cardNumber && cardNumber.length > chars) {
      const len = cardNumber.length;
      const last4 = cardNumber.substring(len - chars);
      maskedCard = 'X'.repeat(len - chars);
      maskedCard += last4;
    }
    return maskedCard;
  }

  static fullName(firstName: string, lastName: string): string {
    let result = '';
    if (firstName) {
      result += firstName;
    }
    if (lastName) {
      result += result ? ' ' + lastName : lastName;
    }
    return result;
  }

  static commaSeparatedFullName(
    firstName?: string,
    lastName?: string,
    middleName?: string
  ): string {
    let result = '';
    if (lastName) {
      result += lastName;
    }
    if (firstName) {
      result += result ? ', ' + firstName : firstName;
    }
    if (middleName) {
      result += result ? ' ' + middleName : middleName;
    }
    return result;
  }

  static formatCurrencyAmount(
    amount: number,
    fix2Decimal?: boolean,
    round?: boolean
  ): string {
    let value: any = 0;
    let prefix = '$';
    const formatterParams: any = {currency: 'USD'};
    if (amount && !isNaN(amount)) {
      value = amount;
    }
    if (fix2Decimal) {
      value = (Math.round(value * 100) / 100).toFixed(2);
      formatterParams.style = 'currency';
      prefix = '';
    } else if (round) {
      value = Math.round(value);
    }
    return (
      prefix + new Intl.NumberFormat('en-US', formatterParams).format(value)
    );
  }

  static formatZipCode(zipCode: string): string | number | undefined | null {
    if (!zipCode) {
      return zipCode;
    }
    return zipCode.toString().replace(/\D/g, '');
  }

  static serializeQueryParams(obj: any = {}): string {
    const str = [];
    for (const key of Object.keys(obj)) {
      str.push(encodeURIComponent(key) + '=' + encodeURIComponent(obj[key]));
    }
    return str.join('&');
  }

  static generateRouteURL({
    hostURL,
    path,
    queryParams
  }: {
    hostURL?: string;
    path?: string;
    queryParams?: any;
  }): string {
    let URL = environment.paymentHost;
    if (hostURL) {
      URL = hostURL;
    }
    if (path) {
      URL += path;
    }
    if (queryParams) {
      URL = `?${HelperUtils.serializeQueryParams(queryParams)}`;
    }
    return URL;
  }
  /** Generate website link with http if http doesn't exist in the string */
  static generateWebsiteLink(website: string): string {
    if (website && !website.startsWith('http')) {
      return `http://${website}`;
    }
    return website;
  }

  static getIndexOfElement(list: any, value: any): any {
    if (!list) {
      return -1;
    } else {
      return list.indexOf(value);
    }
  }

  static getSumOfBalance(dueList: any, key: string | any): any {
    if (dueList?.length == 0) {
      return '-';
    } else {
      let amount = 0;
      for (let i = 0; i < dueList?.length; i++) {
        amount = parseFloat(dueList[i]?.[key]) + amount;
      }
      return amount;
    }
  }

  // Helper function to convert formatted currency string to float
  static formatCurrencyValueToFloat(formattedValue?: string | number): number {
    if (!formattedValue) formattedValue = '0';
    if (typeof formattedValue === 'number') {
      formattedValue = formattedValue.toString();
    }
    // Remove any non-numeric characters except digits, decimal point, and minus sign
    const numericString = formattedValue.replace(/[^0-9.-]/g, '');
    // Parse the numeric string to float
    const floatValue = parseFloat(numericString);
    if (isNaN(floatValue)) {
      return 0;
    }
    return floatValue;
  }

  static checkPatientNameAvailable(patientObject: any): any {
    if (
      patientObject?.first_name ||
      patientObject?.last_name ||
      patientObject?.middle_name
    ) {
      return true;
    }
    return false;
  }

  static containsObject(obj: any, list: any) {
    return list.some((item: any) =>
      Object.keys(item).every(key => item[key] === obj[key])
    );
  }

  static getAppointmentsFilters(list: any, key: string): any {
    const typeFilters: any = [];
    if (!list) {
      return typeFilters;
    } else {
      for (let index = 0; index < list.length; index++) {
        const rawValue =
          key === 'type'
            ? getAppointmentTypeValue(list[index])
            : list[index]?.[key];
        // CHECKING NULL CONDITION
        if (rawValue) {
          const filterObject = {
            key: rawValue,
            label: rawValue
          };
          if (typeFilters?.length == 0) {
            typeFilters.push(filterObject);
          } else {
            const flag = HelperUtils.containsObject(filterObject, typeFilters);
            if (!flag) {
              typeFilters.push(filterObject);
            }
          }
        }
      }
    }
    return typeFilters;
  }

  static getCheck(list: any, key: number | string): boolean {
    if (!list || !key) {
      return false;
    } else {
      return list.indexOf(key) == -1 ? false : true;
    }
  }
  static captilizeFirstLetter(str: string): string {
    if (!str) {
      return '';
    } else {
      return str.charAt(0).toUpperCase() + str.slice(1);
    }
  }

  static formatInputLabel({
    label,
    required = false
  }: {
    label: string;
    required: boolean;
  }): string {
    if (label && required) return `${label} *`;
    return label;
  }
}

export const checkFiltersExist = (list: any) => {
  let flag = false,
    index = 0;
  if (!list) {
    return false;
  }
  while (index < list?.length) {
    if (list[index]) {
      flag = true;
      break;
    }
    index++;
  }
  return flag;
};
export const getStatementStatus = (
  selectedPatient: any,
  isBillingPractice: boolean = false
): typeof StatementStatus[keyof typeof StatementStatus] | null => {
  let originalDueAmount = selectedPatient.original_due_amount;
  let dueAmount = selectedPatient.due_amount;
  let paidDueAmount = selectedPatient.paid_due_amount;
  let isSettled = selectedPatient.is_settled;
  if (isBillingPractice) {
    originalDueAmount = selectedPatient.current_statement_original_due_amount;
    dueAmount = selectedPatient.current_statement_due_amount;
    paidDueAmount = selectedPatient.current_statement_paid_due_amount;
    isSettled = selectedPatient.current_statement_is_settled;
  }
  if (originalDueAmount > 0) {
    // the guarantor has due amount
    if (dueAmount > 0) {
      // There is some due pending
      if (isSettled) {
        // Guarantor was settled
        return StatementStatus.SETTLED;
      } else if (paidDueAmount === 0) {
        // Guarantor has not paid anything yet
        return StatementStatus.UN_PAID;
      } else if (paidDueAmount > 0) {
        // Guarantor paid something
        return StatementStatus.PARTIALLY_PAID;
      }
    } else if (dueAmount === 0) {
      // guarantor has no current due left, but had some due earlier
      if (paidDueAmount === originalDueAmount) {
        // The paid amount equals original due amount
        // then guarantor has paid the balance
        return StatementStatus.PAID;
      }
    }
  }
  return null;
};

// delimiter is underscore _
export function formFileName(...rest: string[]): string {
  const names: string[] = [];
  if (Array.isArray(rest) && rest.length > 0) {
    rest.forEach(r => {
      if (r) names.push(r);
    });
  }
  return names.join('_');
}
// commented for future refernece
// export const getAppointmentFilterResults = (list: any, filters: any) => {
//   const values = Object.values(filters);
//   // CHECKING FILTER SELECTED, IF NOT RETURN ORIGINAL LIST
//   const filtersExist = checkFiltersExist(values);
//   if (!filtersExist) {
//     return list;
//   }

//   // FILTERING THE LIST BASED ON SELECTED VALUES
//   const appointments = list.filter((item: any, apptIndex: number) => {
//     let flag = false;
//     const keys = Object.keys(filters);
//     let index = 0;

//     while (index < keys?.length) {
//       // HANDLING PATIENT SEARCH CASE
//       if (keys[index] === 'search_text' && item?.patient) {
//         // Converting search_text to lower case to have case insensitive search
//         const searchText = filters['search_text'].toLowerCase();

//         // If middle name exists fullname must include firstname,middlename and lastname or if middle name doesn't exist fullname must include firstname and lastname

//         const searchByFullName =
//           item?.patient?.middle_name !== ''
//             ? item?.patient?.first_name
//               ?.toLowerCase()
//               ?.concat(' ', item?.patient?.middle_name?.toLowerCase())
//               ?.concat(' ', item?.patient?.last_name?.toLowerCase())
//               ?.includes(searchText)
//             : item?.patient?.first_name
//               ?.toLowerCase()
//               ?.concat(' ', item?.patient?.last_name?.toLowerCase())
//               ?.includes(searchText);

//         //Enable patient search either by fullname or by the format in which the patient name is being displayed in appointment cards ui
//         flag =
//           searchByFullName ||
//             HelperUtils.commaSeparatedFullName(
//               item?.patient?.first_name?.toLowerCase(),
//               item?.patient?.last_name?.toLowerCase(),
//               item?.patient?.middle_name?.toLowerCase()
//             )?.includes(searchText)
//             ? true
//             : false;
//       } else if (keys[index] === 'vaccine_check' && filters[keys[index]]) {
//         if (typeof item.vaccine_id === 'number') {
//           flag = true;
//         }
//       } else if (
//         filters[keys[index]] !== null &&
//         filters[keys[index]] !== undefined &&
//         filters[keys[index]] !== ''
//       ) {
//         // HANDLING  FILTER TYPE STATEMENT BALANCE CASES;
//         if (filters[keys[index]] === 'BALANCE_GREATER_THAN_ZERO') {
//           const amount = HelperUtils.getSumOfBalance(
//             item?.patient?.guarantor_due,
//             'current_due_amount'
//           );
//           flag = parseInt(amount) > 0 ? true : false;
//         } else if (filters[keys[index]] === 'BALANCE_EQUAL_ZERO') {
//           const amount = HelperUtils.getSumOfBalance(
//             item?.patient?.guarantor_due || [],
//             'current_due_amount'
//           );
//           flag =
//             parseInt(amount) === 0 || isNaN(amount) || amount === '-'
//               ? true
//               : false;
//         } else {
//           // HANDLING REMAINING FILTER TYPES
//           flag = filters[keys[index]] === item[keys[index]] ? true : false;
//         }

//         if (!flag) {
//           break;
//         }
//       }
//       index++;
//     }
//     return flag;
//   });
//   return appointments;
// };

// NEW function for appointment filters
export const getAppointmentFilterResults = (list: any, filters: any) => {
  // CHECKING FILTER SELECTED, IF NOT RETURN ORIGINAL LIST
  const filtersExist = Object.values(filters).some((filter: any) =>
    Array.isArray(filter) ? filter.length : filter !== ''
  );

  if (!filtersExist) {
    return list;
  }

  const getConsentFormStatus = (item: any) => {
    try {
      const value = JSON.parse(item?.consent_meta);
      return value.status;
    } catch (err: any) {
      return '';
    }
  };

  // FILTERING THE LIST BASED ON SELECTED VALUES
  const appointments = list.filter((item: any) => {
    let flag = true;
    const filterKeysToCheck = [
      'eligibility_status',
      'is_manually_verified',
      'patient_forms',
      'status',
      'type',
      'verification_status'
    ];

    // FILTER BY SEARCH TEXT
    if (filters.search_text) {
      const searchText = filters.search_text.toLowerCase();
      const fullName = `${item?.patient?.first_name} ${item?.patient?.middle_name} ${item?.patient?.last_name}`.toLowerCase();
      flag =
        fullName.includes(searchText) ||
        HelperUtils.commaSeparatedFullName(
          item?.patient?.first_name?.toLowerCase(),
          item?.patient?.last_name?.toLowerCase(),
          item?.patient?.middle_name?.toLowerCase()
        )?.includes(searchText);
    }

    // FILTER BY CONSENT FORM STATUS
    if (filters.consent_form_status) {
      flag =
        flag && filters['consent_form_status'] === getConsentFormStatus(item);
    }

    // FILTER BY VACCINE CHECK
    if (filters.vaccine_check && typeof item.vaccine_id === 'number') {
      flag = flag && true;
    } else if (filters.vaccine_check && typeof item.vaccine_id !== 'number') {
      flag = flag && false;
    }

    // FILTER BY PATIENT FEEDBACK
    if (filters.review_type) {
      if (filters.review_type === 'not_reviewed') {
        flag = flag && !item.review_type;
      } else {
        flag = flag && item.review_type == filters.review_type;
      }
    }

    // FILTER BY OTHER FITERS
    for (const key of filterKeysToCheck) {
      if (key === 'eligibility_status') {
        if (filters[key]?.length > 0) {
          flag = flag && filters[key].includes(item[key]);
        }
      } else if (filters[key] && filters[key] !== '') {
        if (key === 'type') {
          flag = flag && filters[key] === getAppointmentTypeValue(item);
        } else {
          flag = flag && filters[key] === item[key];
        }
      }
    }

    if (filters.statement_balance && filters.statement_balance !== '') {
      const amount = HelperUtils.getSumOfBalance(
        item?.patient?.guarantor_due || [],
        'current_due_amount'
      );
      if (filters.statement_balance === 'BALANCE_GREATER_THAN_ZERO') {
        flag = flag && parseInt(amount) > 0;
      } else if (filters.statement_balance === 'BALANCE_EQUAL_ZERO') {
        flag =
          flag && (parseInt(amount) === 0 || isNaN(amount) || amount === '-');
      }
    }

    return flag;
  });

  return appointments;
};

export const sortByPriority = (list: any): any => {
  if (!list) {
    return [];
  }
  list.sort(function (a: any, b: any) {
    return (
      MiscChargeTypesPriorityOrder[a.charge_type as 'patient copay payment'] -
      MiscChargeTypesPriorityOrder[b.charge_type as 'patient copay payment']
    );
  });

  return list;
};

export const calculateEachChargeTypeAmount = (chargeList: any) => {
  const individualChargeAmounts: any[] = [];
  if (!chargeList?.length) {
    return individualChargeAmounts;
  } else {
    chargeList.forEach((element: any) => {
      const obj: any = {
        charge_type: element?.charge_type
      };
      let amount: any = 0;
      element?.balance_amount?.forEach((balance: number) => {
        amount += balance;
      });
      obj.balance_amount = amount;
      individualChargeAmounts.push(obj);
    });
  }

  // individualChargeAmounts.sort((a, b) => {
  //   if (
  //     a.charge_type == PaymentMethods.PATIENT_COPAY_PAYMENT ||
  //     b.charge_type == PaymentMethods.PATIENT_COPAY_PAYMENT
  //   ) {
  //     return 1;
  //   } else {
  //     return b.charge_type > a.charge_type ? 1 : -1;
  //   }
  // });
  return individualChargeAmounts;
};

export const groupByChargeType = (list: any) => {
  let output: any = [];
  if (!list.length) {
    return output;
  }
  output = list.reduce(function (total: any, cur: any) {
    // Get the index of the key-value pair.
    const occurs = total.reduce(function (n: any, item: any, i: any) {
      return item.charge_type === cur.charge_type ? i : n;
    }, -1);

    // If the name is found,
    if (occurs >= 0) {
      // append the current value to its list of values.
      total[occurs].balance_amount = total[occurs].balance_amount.concat(
        cur.balance_amount
      );
      // Otherwise,
    } else {
      // add the current item to o (but make sure the value is an array).
      const obj = {
        charge_type: cur.charge_type,
        balance_amount: [cur.balance_amount]
      };
      total = total.concat([obj]);
    }

    return total;
  }, []);

  output = sortByPriority(output);

  return calculateEachChargeTypeAmount(output);
};

export const checkClinicShortCodeExist = (
  clinicsList: any,
  selectedClinic: any
) => {
  if (!clinicsList && clinicsList.length) {
    return;
  }
  if (!selectedClinic) {
    return;
  }
  const matchedClinic: any = clinicsList.find((clinic: any) => {
    return selectedClinic.includes(clinic.id);
  });
  return matchedClinic?.short_code;
};

export const formNumberOfInstallments = (maxInstallments: number) => {
  const totalInstallmentOptions = [];
  for (
    let index = minimumPaymentPlanInstallments;
    index <= maxInstallments;
    index++
  ) {
    const object = {
      name: index,
      value: index
    };
    totalInstallmentOptions.push(object);
  }
  return totalInstallmentOptions;
};

export const paymentPlanStatusText = (statusValue: string) => {
  let mappedText = '';
  if (!statusValue) {
    return '';
  } else {
    switch (statusValue) {
      case PaymentPlanStatus.IN_PROGRESS: {
        mappedText = 'PAYMENT_PLANS.ACTIVE';
        break;
      }
      case PaymentPlanStatus.CANCELLED: {
        mappedText = 'PAYMENT_PLANS.CANCELLED';
        break;
      }
      case PaymentPlanStatus.COMPLETED: {
        mappedText = 'PAYMENT_PLANS.COMPLETED';
        break;
      }
      default: {
        mappedText = '';
      }
    }
  }
  return mappedText;
};
export const convertToAmount = (amount: number | string) => {
  if (typeof amount === 'string') {
    return +amount.replace(/\$/g, '').replace(/,/g, '');
  } else if (typeof amount === 'number') {
    return amount;
  } else {
    return 0;
  }
};

export const getNextInstallmentDate = (instalment_start_date: string) => {
  if (!instalment_start_date) {
    return '';
  } else {
    const startDate = moment(instalment_start_date).format('YYYY-MM-DD');
    const nextDate = moment(startDate).add(1, 'months').format('YYYY-MM-DD');
    return nextDate;
  }
};

export const calculateAgeFromDOB = (user_dob: any, t: any) => {
  try {
    let ageString = '';
    if (user_dob) {
      const dob = new Date(user_dob),
        dobYear = dob.getFullYear(),
        dobMonth = dob.getMonth(),
        dobDate = dob.getDate();
      const now = new Date();
      const currentYear = now.getFullYear(),
        currentMonth = now.getMonth(),
        currentDate = now.getDate();
      let yearAge = currentYear - dobYear,
        monthAge = 0,
        dateAge = 0;
      // get months
      if (currentMonth >= dobMonth) {
        // get months when current month is greater
        monthAge = currentMonth - dobMonth;
      } else {
        yearAge--;
        monthAge = 12 + currentMonth - dobMonth;
      }
      // get days
      if (currentDate >= dobDate) {
        // get days when the current date is greater
        dateAge = currentDate - dobDate;
      } else {
        monthAge--;
        dateAge = 31 + currentDate - dobDate;
        if (monthAge < 0) {
          monthAge = 11;
          yearAge--;
        }
      }
      // group the age in a single variable
      const age = {
        years: yearAge,
        months: monthAge,
        days: dateAge
      };
      if (age.years > 0 && age.months > 0 && age.days > 0) {
        ageString = t('COMMON.NO_OF_YEARS_MONTHS', {
          years: age.years,
          months: age.months
        });
      } else if (age.years == 0 && age.months == 0 && age.days > 0) {
        ageString = t('COMMON.NO_OF_DAYS', {
          days: age.days
        });
      }
      // when current month and date is same as birth date and month
      else if (age.years > 0 && age.months == 0 && age.days == 0) {
        ageString = t('COMMON.NO_OF_YEARS', {
          years: age.years
        });
      } else if (age.years > 0 && age.months > 0 && age.days == 0) {
        ageString = t('COMMON.NO_OF_YEARS_MONTHS', {
          years: age.years,
          months: age.months
        });
      } else if (age.years == 0 && age.months > 0 && age.days > 0) {
        ageString = t('COMMON.NO_OF_MONTHS_DAYS', {
          months: age.months,
          days: age.days
        });
      } else if (age.years > 0 && age.months == 0 && age.days > 0) {
        ageString = t('COMMON.NO_OF_YEARS', {
          years: age.years
        });
      } else if (age.years == 0 && age.months > 0 && age.days == 0) {
        ageString = t('COMMON.NO_OF_MONTHS', {
          months: age.months
        });
      }
    }
    return ageString;
  } catch (e: any) {
    throw new Error(e.message);
  }
};

export function getAppointmentStatus(status: string): string {
  const [item] = AppointmentStatusMapping.filter(item1 => item1.id === status);
  if (item) {
    return item.name;
  }
  return status;
}

export function getAppointmentTypeValue(appointment: any): string {
  if (!appointment) {
    return '';
  }
  return appointment?.appointment_type?.label || appointment?.type || '';
}

export function getAppointmentTypeLabel(typeValue: string): string {
  if (!typeValue) {
    return '';
  }
  if (typeValue in APPOINTMENT_TYPE_TRANS_KEY) {
    return APPOINTMENT_TYPE_TRANS_KEY[
      typeValue as keyof typeof APPOINTMENT_TYPE_TRANS_KEY
    ];
  }
  return typeValue;
}
export function checkIfNaturalNumber(number: string): boolean {
  const isDigit = new RegExp(/^\d*$/g);
  if (typeof number !== 'string') {
    return false;
  }
  const numberValue = Number(number);
  const numberStringValue = Number(number).toString();
  return (
    isDigit.test(numberStringValue) &&
    numberValue > 0 &&
    Math.floor(numberValue) == numberValue
  );
}

export function getInstalmentDates({
  frequency,
  selected_week_day,
  number_of_instalments,
  instalment_start_date,
  start_payment_plan_today = false
}: any): any {
  const instalmentDates = [];
  let installmentStartDate: any = '';
  let frequencyTypeText: any = '';
  let multiplyIndex = 1;
  let nextDate = '';

  const currentDate = moment().format('YYYY-MM-DD');

  const dayNumber = moment().day();
  if (frequency === PAYMENT_PLAN_FREQUENCY_IDS.MONTHLY) {
    installmentStartDate = moment(instalment_start_date);
    frequencyTypeText = 'months';
  } else if (
    frequency === PAYMENT_PLAN_FREQUENCY_IDS.WEEKLY ||
    frequency === PAYMENT_PLAN_FREQUENCY_IDS.BI_WEEKLY
  ) {
    // CASE TO HANDLE BI-WEEKLY FREQUENCY
    if (frequency === PAYMENT_PLAN_FREQUENCY_IDS.BI_WEEKLY) {
      multiplyIndex = 2;
    }
    frequencyTypeText = 'weeks';

    // CASE:1 SELECTED DAY EQUAL TO TODAY'S DAY
    if (dayNumber === selected_week_day) {
      if (start_payment_plan_today) {
        installmentStartDate = moment();
      } else {
        installmentStartDate = moment()
          .add(1, 'weeks')
          .isoWeekday(selected_week_day);
      }
    } else if (selected_week_day < dayNumber) {
      // CASE 2: SELECTED DAY IS PAST DAY OF WEEK DAYS
      installmentStartDate = moment()
        .add(1, 'weeks')
        .isoWeekday(selected_week_day);
    } else if (selected_week_day > dayNumber) {
      // CASE 2: SELECTED DAY IS FUTURE DAY OF WEEK DAYS
      installmentStartDate = moment().add(
        selected_week_day - dayNumber,
        'days'
      );
    }
  }
  // LOOP TO GET PAYMENT PLAN DATES
  for (let i = 0; i < number_of_instalments; i++) {
    const instalmentDate = moment(installmentStartDate)
      .add(i * multiplyIndex, frequencyTypeText)
      .format('YYYY-MM-DD');
    instalmentDates.push(instalmentDate);
  }

  nextDate = instalmentDates[0];
  if (start_payment_plan_today || currentDate === instalment_start_date) {
    nextDate = instalmentDates[1];
  }

  return {
    startDate: instalmentDates[0],
    nextDate: nextDate,
    endDate: instalmentDates[instalmentDates.length - 1],
    instalmentDates
  };
}

export function mapAppointmentStatus(status: string): string {
  let appointmentStatusName = status;
  const filteredAppointmentStatuses = PatientRecallAppointmentStatusMapping.filter(
    appointmentStatus => {
      return appointmentStatus.id === status;
    }
  );
  if (filteredAppointmentStatuses.length > 0) {
    const [firstAppointmentMatchedStatus] = filteredAppointmentStatuses;
    appointmentStatusName = firstAppointmentMatchedStatus.name;
  }
  return appointmentStatusName;
}

export function fixTo2DecimalNumber(value: any) {
  if (isNaN(value)) {
    return value;
  }
  return Math.round(value * 100) / 100;
}

export function retryPaymentPlanFlag(instalment_date: any) {
  if (!instalment_date) {
    return false;
  }
  const newMoment = moment().format('YYYY-MM-DD');
  const instalmentMoment = moment(instalment_date).format('YYYY-MM-DD');
  if (instalmentMoment < newMoment) {
    return true;
  }
  return false;
}

export function getNotificationTypeLabel(type: string) {
  if (!type) {
    return '';
  }
  const typeMappingText = type.toLowerCase();
  let notificationTypeLabel = '';

  switch (typeMappingText) {
    case NotificationType.SMS:
      notificationTypeLabel = 'COMMON.TEXT';
      break;
    case NotificationType.EMAIL:
      notificationTypeLabel = 'COMMON.EMAIL';
      break;
    default:
      notificationTypeLabel = '';
  }
  return notificationTypeLabel;
}

export function formOptedOutNotificationLabel(optedOutNotifications: string) {
  if (!optedOutNotifications) {
    return '';
  }
  const optedOutLabel = optedOutNotifications.replace('sms', 'text');
  return optedOutLabel;
}

export const formMessageConversationActions = (message: any, t: any) => {
  if (!message) {
    return [];
  }

  const messageActions: any = [];

  /**
   * CHECKING MESSAGE STATUS AND SHOWING MARK AND NOT DONE OPTION
   */
  if (message?.event_type_status === ReminderWorkFlowStatusTypes.COMPLETED) {
    messageActions.push({
      name: t('MESSAGE_CONVERSATION_ACTIONS.MARK_AS_NOT_DONE'),
      id: 'mark_as_not_done'
    });
  }
  // else {
  //   messageActions.push({
  //     name: t('MESSAGE_CONVERSATION_ACTIONS.MARK_AS_DONE'),
  //     id: 'mark_as_done'
  //   });
  // }
  /**
   * CHECKING MESSAGE CATEGORY
   * CASE1: CATEGORY IS CLINICAL, SHOW CHANGE TO NON CLINICAL OPTION
   * CASE2: CATEGORY IS NON CLINICAL, SHOW CHANGE TO CLINICAL OPTION
   * CASE3: CATEGORY IS EMPTY SHOW CLINICAL, NON CLINICAL OPTIONS
   */

  if (message?.message_category === messagesCategory.CLINICAL) {
    messageActions.push({
      name: t('MESSAGE_CONVERSATION_ACTIONS.CHANGE_TO_NON_CLINICAL_MESSAGE'),
      id: 'change_to_not_clinical_message'
    });
  } else if (message?.message_category === messagesCategory.BILLING) {
    messageActions.push({
      name: t('MESSAGE_CONVERSATION_ACTIONS.CHANGE_TO_CLINICAL_MESSAGE'),
      id: 'change_to_clinical_message'
    });
  } else {
    messageActions.push({
      name: t('MESSAGE_CONVERSATION_ACTIONS.CHANGE_TO_CLINICAL_MESSAGE'),
      id: 'change_to_clinical_message'
    });
    messageActions.push({
      name: t('MESSAGE_CONVERSATION_ACTIONS.CHANGE_TO_NON_CLINICAL_MESSAGE'),
      id: 'change_to_not_clinical_message'
    });
  }

  /**
   * ADDING MARK AS NEW OPTION
   */

  if (message?.is_read) {
    messageActions.push({
      name: t('MESSAGE_CONVERSATION_ACTIONS.MARK_AS_NEW'),
      id: 'mark_as_new'
    });
  }

  return messageActions;
};

export function nFormatter(num: number): string {
  const suffixes = ['', 'K', 'M', 'B'];
  let suffixIndex = 0;
  let remainder = 0;
  let tempNumber = '';

  while (num >= 1000 && suffixIndex < suffixes.length - 1) {
    remainder = num % 1000;
    num /= 1000;
    suffixIndex++;
  }
  tempNumber = num.toLocaleString();
  if (tempNumber.indexOf('.') > -1) {
    tempNumber = tempNumber.substring(0, tempNumber.indexOf('.'));
  }
  tempNumber += suffixes[suffixIndex];
  if (remainder > 0) {
    tempNumber += '+';
  }
  return tempNumber;
}

// To get slot type(morning or afternoon)
export const getSlotType = (timeString: string) => {
  const time = moment(timeString, 'h:mm A');
  const onePM = moment('1:00 PM', 'h:mm A');
  return time.isBefore(onePM, 'hour') ? 'morning' : 'afternoon';
};

// To get slot type index(0 for morning, 1 for afternoon)
export const getSlotTypeIndex = (timeString: string) => {
  const time = moment(timeString, 'h:mm A');
  const onePM = moment('1:00 PM', 'h:mm A');
  return time.isBefore(onePM, 'hour') ? 0 : 1;
};

export function isValidDateCheck(dateString: string): boolean {
  const date = new Date(dateString);
  return !isNaN(date.getTime()); // Returns true if the date is valid
}

export function isValidJSON(jsonString: string): boolean {
  try {
    JSON.parse(jsonString);
    return true;
  } catch (error) {
    return false;
  }
}

// To mask the dob
export function getMaskedDOB(dob: string): string {
  if (!dob || dob.length < 8) {
    return dob; // or throw an error if an 8-character string is expected
  }
  const year = dob.slice(-2);
  return `xx-xx-xx${year}`;
}

// To mask the SSN when hidden: show last digit only (e.g. ***5). If no ssn, return ***5 as placeholder.
export function getMaskedSSN(ssn?: string | null): string {
  const lastDigit =
    ssn != null && String(ssn).length > 0 ? String(ssn).slice(-1) : null;
  return lastDigit != null ? `***${lastDigit}` : '****';
}

export function decryptValues(
  value: string | CryptoJS.lib.CipherParams,
  key: string | CryptoJS.lib.WordArray
): any {
  try {
    const bytes = CryptoJS.AES.decrypt(value, key);
    const decryptedData = bytes.toString(CryptoJS.enc.Utf8);
    return decryptedData;
  } catch (error) {
    return '';
  }
}

export const formatReasonLabel = (value: any): string => {
  if (value == null || value === '') return '-';

  return String(value)
    .replace(/[_-]+/g, ' ') // replace _ and - with space
    .replace(/\s+/g, ' ') // remove extra spaces
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, char => char.toUpperCase()); // capitalize words
};

const EMR_ALPHANUMERIC_INVALID_CHARS = /[^A-Za-z0-9]/g;

export const sanitizeEmrAlphanumericInput = (value: string): string =>
  (value || '').replace(EMR_ALPHANUMERIC_INVALID_CHARS, '');

export default HelperUtils;
